/**
 * CountFormatControl — the unit a person counts in, chosen in one tap.
 *
 * The count field used to be labelled in the item's normalized unit, because
 * that is the unit everything downstream compares in. It is the wrong unit to
 * put in front of somebody holding a tub: nobody counts mayonnaise by the fluid
 * ounce, and "10.125 gal" is a conversion done in their head, at speed, that
 * nobody can check afterwards.
 *
 * So this control names the physical package the line is counted in — "Sysco —
 * jar (12 floz)" — and opens the alternatives directly. That directness is the
 * point. The previous affordance was a "Count by package" link that opened a
 * sheet, which meant two taps and a modal to answer "what am I holding", and it
 * only appeared once somebody knew to look for it.
 *
 * What it does NOT do is choose a vendor to buy from. A restaurant may count
 * Costco tubs all week and order from Sysco; those are two decisions and they
 * live in two columns (`count_display_purchase_option_id` here,
 * `selected_purchase_option_id` for ordering). Nothing in this file writes the
 * ordering one.
 *
 * `readOnly` renders the current package as passive context with no menu. Some
 * summary hosts use that form; the live count card keeps the menu because the
 * package physically on the shelf may differ from the vendor selected for the
 * next order.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  countFormatChoices,
  countFormatSelection,
  describeRungEquivalence,
  PRESENTATION_SOURCE_TEXT,
} from '../utils/countPresentation.js';
import { MISSING } from '../utils/countUnitModel.js';

const BORDER = '#C8B5A0';
const INK = '#5A4A42';
const MUTED = '#8B7B73';
const ACCENT = '#8A5A00';

// Implemented in utils/countPresentation (rungs in, text out — nothing is
// re-interpreted in React); re-exported so existing imports keep working.
export { describeRungEquivalence };

/**
 * The on-hand total expressed back in the chosen format, e.g. 832 floz shown as
 * "5.78 cases". Rounded for display only — the stored quantity is never rounded.
 */
export function equivalentInFormat(normalizedQuantity, rung) {
  if (normalizedQuantity == null || !rung?.qtyInCountUnit) return null;
  const value = Number(normalizedQuantity) / Number(rung.qtyInCountUnit);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100) / 100;
}

export default function CountFormatControl({
  presentation,
  countUnit,
  onSelectFormat,
  onToggleFollowLatest,
  onOpenPackageSheet,
  entryCount = 0,
  compact = false,
  disabled = false,
  // The count card's meta grid supplies its own "Count as" cell label; keeping
  // the internal caption there would say it twice.
  hideLabel = false,
  // Passive display for summary-only hosts.
  readOnly = false,
  // A selectable host may already render Vendor in a neighboring cell. In
  // that layout the trigger should say "case"/"bag", not repeat "Sysco".
  showPackageLabel = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const choices = useMemo(
    () => countFormatChoices(presentation, { countUnit }),
    [presentation, countUnit],
  );
  const current = presentation?.rung ?? null;
  // The count card is width-constrained: the trigger shows just the vendor
  // name ("Shamrock Foods"), not the full rung + pack description ("Shamrock
  // Foods — case (20 lb average case)") that overflowed it. A rung label is
  // "<vendor> — <unit>" whenever a vendor is known (countPresentation.js /
  // packageLadder.mjs compose it), so the vendor is its first segment; the
  // dropdown menu keeps the full per-rung labels for disambiguation, and the
  // counted unit rides with the price at the top of the card.
  const rungLabel = current?.label || '';
  const vendorOnly = rungLabel.includes(' — ') ? rungLabel.split(' — ')[0].trim() : '';
  // Hosts with a separate Vendor cell name the PACKAGE ("case") here and use
  // the equivalence line to size it.
  const label = (readOnly || showPackageLabel)
    ? (current?.unitLabel || presentation?.label || countUnit || MISSING)
    : (vendorOnly || presentation?.label || countUnit || MISSING);
  const equivalence = describeRungEquivalence(current, countUnit, presentation?.rungs);

  // With only one usable count rung there is nothing to choose between, and a
  // dropdown that opens on a single choice is a lie about the options
  // available — it also costs a tap on every line of every count. The package
  // still shows, as passive context. (needsSelector keys on RUNGS: one option
  // whose case holds 12 bottles is two ways to count, and this opens.)
  const hasChoice = !readOnly && presentation?.needsSelector !== false && choices.length > 1;

  useEffect(() => {
    if (!open) return undefined;
    const onAway = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onAway);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (choice) => {
    if (choice.disabled) return;
    setOpen(false);
    // Picking a format IS pinning it — otherwise the next verified invoice
    // would quietly undo the choice the user just made.
    onSelectFormat?.(countFormatSelection(choice, { countUnit }), choice);
  };

  if (!hasChoice) {
    return (
      <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, maxWidth: '100%' }}>
        {!hideLabel && (
          <span
            style={{
              fontSize: '0.5rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: MUTED, lineHeight: 1.1,
            }}
          >
            Count as
          </span>
        )}
        <span
          data-count-format-static="1"
          style={{
            fontSize: compact ? '0.72rem' : '0.8rem', fontWeight: 700, color: INK,
            lineHeight: 1.15, maxWidth: '100%',
          }}
        >
          {label}
        </span>
        {equivalence && (
          <span style={{ fontSize: '0.62rem', color: MUTED }}>{equivalence}</span>
        )}
        {/* An item can have one countable package AND other packages nobody has
            sized. Those must not vanish just because there is nothing to choose
            between — somebody who bought from that vendor needs to see why they
            cannot count in it, and where to fix it. */}
        {(presentation?.unresolved || []).length > 0 && (
          <span
            data-count-format-unresolved="1"
            style={{ fontSize: '0.62rem', color: ACCENT, fontWeight: 700 }}
          >
            {presentation.unresolved.length} package
            {presentation.unresolved.length === 1 ? '' : 's'} need
            {presentation.unresolved.length === 1 ? 's' : ''} package review
          </span>
        )}
        {/* Mixed-package entry used to live in the dropdown; a read-only cell
            must not take it with it — a shelf mid-transition still holds two
            packagings at once. */}
        {readOnly && onOpenPackageSheet && current && current.level !== 'canonical' && (
          <button
            type="button"
            data-count-format-sheet-link="1"
            onClick={onOpenPackageSheet}
            style={{
              background: 'transparent', border: 'none', padding: 0,
              font: 'inherit', fontSize: '0.62rem', fontWeight: 700,
              color: ACCENT, textDecoration: 'underline', textUnderlineOffset: 2,
              cursor: 'pointer', textAlign: 'left',
            }}
          >
            Count several packages{entryCount > 0 ? ` (${entryCount})` : ''}
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Count as ${label}. Tap to change.`}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
          background: '#FFFFFF',
          border: `1px solid ${BORDER}`,
          borderRadius: 10,
          padding: compact ? '3px 8px' : '5px 10px',
          cursor: disabled ? 'default' : 'pointer',
          font: 'inherit',
          maxWidth: '100%',
          minHeight: 34,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {!hideLabel && (
          <span
            style={{
              fontSize: '0.5rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: MUTED,
              lineHeight: 1.1,
            }}
          >
            Count as
          </span>
        )}
        <span
          style={{
            fontSize: compact ? '0.72rem' : '0.8rem',
            fontWeight: 700,
            color: INK,
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {label} <span style={{ color: MUTED, fontWeight: 600 }}>▾</span>
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Count as"
          style={{
            position: 'absolute',
            zIndex: 40,
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: 240,
            maxWidth: 320,
            maxHeight: 320,
            overflowY: 'auto',
            background: '#FFFFFF',
            border: `1px solid ${BORDER}`,
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
            padding: 6,
          }}
        >
          {/* Counting language, not ordering language. This control chooses
              what is in your hand; it does not choose a supplier. */}
          <div
            style={{
              padding: '4px 9px 7px',
              fontSize: '0.72rem',
              fontWeight: 800,
              color: INK,
              borderBottom: `1px solid ${BORDER}`,
              marginBottom: 5,
            }}
          >
            What are you counting?
          </div>

          {choices.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: '0.75rem', color: MUTED }}>
              No confirmed package count options are available.
            </div>
          )}

          {choices.map((choice) => (
            <button
              key={choice.key}
              type="button"
              role="option"
              aria-selected={choice.selected}
              aria-disabled={choice.disabled}
              disabled={choice.disabled}
              onClick={() => choose(choice)}
              title={choice.disabled ? choice.reason || undefined : undefined}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: choice.selected ? '#F5E2D0' : 'transparent',
                border: 'none',
                borderRadius: 8,
                padding: '7px 9px',
                font: 'inherit',
                fontSize: '0.78rem',
                color: choice.disabled ? MUTED : INK,
                cursor: choice.disabled ? 'not-allowed' : 'pointer',
                opacity: choice.disabled ? 0.7 : 1,
                minHeight: 34,
              }}
            >
              <span style={{ fontWeight: choice.selected ? 800 : 600 }}>
                {choice.label}
                {choice.selected && (
                  // "Counting", never "Selected" — on a screen that also has an
                  // ordering vendor picker, "Selected" reads as the supplier.
                  <span
                    style={{
                      marginLeft: 6, fontSize: '0.55rem', fontWeight: 800,
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      background: '#E8DDD0', color: INK, borderRadius: 999,
                      padding: '1px 6px', whiteSpace: 'nowrap',
                    }}
                  >
                    Counting
                  </span>
                )}
              </span>
              {/* The package itself, because the vendor name alone does not say
                  what is in your hand — the same vendor may appear twice. */}
              {(choice.packDescription || choice.vendorSku) && (
                <span style={{ display: 'block', fontSize: '0.68rem', color: MUTED }}>
                  {choice.packDescription}
                  {choice.packDescription && choice.vendorSku ? ' · ' : ''}
                  {choice.vendorSku ? `SKU ${choice.vendorSku}` : ''}
                </span>
              )}
              {choice.qtyInCountUnit != null && choice.level !== 'canonical' && (
                <span style={{ display: 'block', fontSize: '0.68rem', color: MUTED }}>
                  {/* The same discrete-first equivalence as the row: "1 case =
                      12 jars" checks against the case label; the measured form
                      stands where no inner rung exists. */}
                  {describeRungEquivalence(choice, countUnit, presentation?.rungs)}
                </span>
              )}
              {choice.isDefaultPackage && choice.defaultReason && !choice.disabled && (
                <span style={{ display: 'block', fontSize: '0.66rem', color: ACCENT, fontWeight: 700 }}>
                  {choice.defaultReason}
                </span>
              )}
              {choice.disabled && choice.reason && (
                <span style={{ display: 'block', fontSize: '0.66rem', color: MUTED }}>{choice.reason}</span>
              )}
            </button>
          ))}

          <div style={{ borderTop: `1px solid ${BORDER}`, margin: '6px 4px 4px', paddingTop: 6 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 7,
                padding: '2px 5px 6px',
                fontSize: '0.72rem',
                color: INK,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={presentation?.followLatest !== false}
                onChange={(e) => onToggleFollowLatest?.(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                <span style={{ fontWeight: 700 }}>Follow latest purchase</span>
                <span style={{ display: 'block', color: MUTED, fontSize: '0.68rem' }}>
                  {presentation?.followLatest !== false
                    ? 'A newer verified purchase can change this format.'
                    : 'Your choice stays until you change it.'}
                </span>
              </span>
            </label>

            {onOpenPackageSheet && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onOpenPackageSheet();
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: '6px 5px',
                  font: 'inherit',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  color: ACCENT,
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                  cursor: 'pointer',
                  minHeight: 32,
                }}
              >
                Count several packages at once
                {entryCount > 0 ? ` (${entryCount})` : ''}
              </button>
            )}
          </div>
        </div>
      )}

      {/* The equivalence rides under the trigger in every density — compact or
          not — because the count card IS where somebody checks "1 case = 12
          bottles" against the case in their hands. */}
      {equivalence && (
        <div style={{ fontSize: '0.62rem', color: MUTED, marginTop: 2, whiteSpace: 'nowrap' }}>
          {equivalence}
        </div>
      )}

      {presentation?.warnings?.length > 0 && (
        <div style={{ fontSize: '0.62rem', color: '#B3261E', marginTop: 2 }}>
          {presentation.warnings[0]}
        </div>
      )}

      {presentation?.source && !compact && (
        <span style={{ display: 'none' }} data-format-source={presentation.source}>
          {PRESENTATION_SOURCE_TEXT[presentation.source]}
        </span>
      )}
    </div>
  );
}
